import { Link } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../tailgrids/core/collapsible.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '../tailgrids/core/tooltip.js';
import { cn } from '../../utils/cn.js';
import type { NavItemWithModule } from '../../modules/useNavItems.js';
import { resolveIcon } from '../../modules/icons.js';
import type { NavGroupNode } from './nav-tree.js';
import { AltArrowUpIcon } from './shell-icons.js';

const ACTIVE = 'bg-sidebar-navigation-nav-item-nav-hover-background text-text-primary';
const IDLE =
  'text-text-secondary transition-colors duration-200 hover:bg-sidebar-navigation-nav-item-nav-hover-background hover:text-text-primary';

interface CommonProps {
  activePath: string | null;
  onNavigate?: () => void;
}

/** Collapsed rail: an icon with the label in a tooltip. */
function RailLink({
  to,
  label,
  icon,
  active,
  onNavigate,
}: {
  to: string;
  label: string;
  icon?: string;
  active: boolean;
  onNavigate?: () => void;
}) {
  const Icon = resolveIcon(icon);
  return (
    <div className="flex justify-center">
      <Tooltip placement="right">
        <TooltipTrigger asChild>
          <Link
            to={to}
            onClick={onNavigate}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex items-center justify-center rounded-lg px-3 py-2.5',
              active
                ? 'bg-sidebar-navigation-nav-item-nav-hover-background text-icon-primary'
                : 'text-icon-tertiary transition-colors duration-200 hover:bg-sidebar-navigation-nav-item-nav-hover-background hover:text-icon-primary',
            )}
          >
            <Icon size={20} />
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export function ShellNavLink({
  item,
  collapsed,
  activePath,
  onNavigate,
}: CommonProps & { item: NavItemWithModule; collapsed: boolean }) {
  const active = activePath === item.path;
  if (collapsed) {
    return <RailLink to={item.path} label={item.label} icon={item.icon} active={active} onNavigate={onNavigate} />;
  }
  const Icon = resolveIcon(item.icon);
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium', active ? ACTIVE : IDLE)}
    >
      <span className={active ? 'text-icon-primary' : 'text-icon-tertiary'}>
        <Icon size={18} />
      </span>
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

/**
 * Template example: NextAdmin `components/common/sidebar/nav-item.tsx` (commit e0b72a9aa69e).
 * A nav group: collapsible with its pages as children when expanded; on the collapsed rail, one
 * icon that opens the active page of the group (or its first page).
 */
export function ShellNavGroup({
  group,
  collapsed,
  activePath,
  onNavigate,
}: CommonProps & { group: NavGroupNode; collapsed: boolean }) {
  const activeItem = group.items.find((item) => item.path === activePath);
  if (collapsed) {
    const target = activeItem ?? group.items[0];
    return (
      <RailLink
        to={target.path}
        label={group.label}
        icon={group.icon}
        active={activeItem !== undefined}
        onNavigate={onNavigate}
      />
    );
  }

  const Icon = resolveIcon(group.icon);
  const hasActive = activeItem !== undefined;
  return (
    <Collapsible id={group.label} className="max-w-none border-none bg-transparent data-expanded:pb-0!">
      <CollapsibleTrigger
        className={cn(
          'group/collapsible flex w-full items-center justify-between gap-3 rounded-lg border-none bg-transparent px-3 py-2 text-sm font-medium sm:px-3 sm:py-2',
          hasActive ? ACTIVE : IDLE,
        )}
      >
        <span className="flex flex-1 items-center gap-3">
          <span
            className={cn(
              hasActive
                ? 'text-icon-primary'
                : 'text-icon-tertiary transition-colors duration-200 group-hover/collapsible:text-icon-primary',
            )}
          >
            <Icon size={18} />
          </span>
          <span className="truncate">{group.label}</span>
        </span>
        <AltArrowUpIcon className="rotate-180 text-icon-tertiary duration-200 group-data-expanded:rotate-0" />
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-1 pr-0 group-data-expanded:mt-2">
        {group.items.map((item) => {
          const active = item.path === activePath;
          return (
            <div key={item.path} className="px-0">
              <Link
                to={item.path}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'block truncate rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  active ? ACTIVE : IDLE,
                )}
              >
                {item.label}
              </Link>
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}
